<script setup lang="ts">
import { computed } from 'vue';
import Calendar from 'primevue/calendar';

interface Props {
  modelValue?: Date | string | null;
  placeholder?: string;
  showIcon?: boolean;
  dateFormat?: string;
  showTime?: boolean;
  hourFormat?: string;
  disabled?: boolean;
  showButtonBar?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: 'Select Date',
  showIcon: true,
  dateFormat: 'yy-mm-dd',
  showTime: false,
  hourFormat: '24',
  disabled: false,
  showButtonBar: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: Date | string | null];
}>();

// Convert incoming value (string from API) to Date object for Calendar
const internalValue = computed({
  get() {
    if (!props.modelValue) return null;
    if (props.modelValue instanceof Date) return props.modelValue;

    // Parse YYYY-MM-DD string to Date at local midnight
    const [year, month, day] = props.modelValue.split('-').map(Number);
    return new Date(year, month - 1, day);
  },
  set(value: Date | null) {
    if (!value) {
      emit('update:modelValue', null);
      return;
    }

    // Guard against non-Date values
    if (!(value instanceof Date) || isNaN(value.getTime())) {
      emit('update:modelValue', null);
      return;
    }

    // Convert Date to YYYY-MM-DD string to avoid timezone issues
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    emit('update:modelValue', dateString);
  },
});
</script>

<template>
  <Calendar
    v-model="internalValue"
    :placeholder="placeholder"
    :show-icon="showIcon"
    :date-format="dateFormat"
    :show-time="showTime"
    :hour-format="hourFormat"
    :disabled="disabled"
    :show-button-bar="showButtonBar"
    :min-date="minDate"
    :max-date="maxDate"
  />
</template>